const HORIZON_COLLECTION_ELEMENT = 'horizon-collection-results';
const DESKTOP_POPOVER_SELECTOR = '.horizon-collection-facets--horizontal [data-horizon-facet-popover], [data-horizon-sort-popover]';
const COLLECTION_PARAMETER_EXCLUSIONS = new Set(['q', 'section_id']);
const FORM_PARAMETER_EXCLUSIONS = new Set([...COLLECTION_PARAMETER_EXCLUSIONS, 'page']);

function sanitizeCollectionURL(value, origin = window.location.origin) {
  const url = new URL(value, origin);

  for (const parameter of COLLECTION_PARAMETER_EXCLUSIONS) {
    url.searchParams.delete(parameter);
  }

  return url;
}

function collectionURLFromEntries(action, entries, origin = window.location.origin) {
  const url = sanitizeCollectionURL(action, origin);
  url.search = '';

  for (const [name, value] of entries) {
    if (FORM_PARAMETER_EXCLUSIONS.has(name) || String(value).trim() === '') continue;
    url.searchParams.append(name, String(value));
  }

  return url;
}

class HorizonCollectionResults extends HTMLElement {
  #abortController;
  #mobileMedia;
  #gridDensity = 'comfortable';

  connectedCallback() {
    this.#mobileMedia = window.matchMedia('(max-width: 749px)');
    this.querySelector('[data-horizon-collection-form]')?.setAttribute('data-enhanced', '');
    const drawer = this.querySelector('[data-horizon-filter-drawer]');
    if (drawer && !this.#mobileMedia.matches) drawer.open = true;
    const controls = this.querySelector('[data-horizon-grid-controls]');
    if (controls) controls.hidden = false;
    document.addEventListener('click', this.#handleOutsideClick);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('toggle', this.#handleDrawerToggle, true);
    this.#mobileMedia.addEventListener('change', this.#handleViewportChange);
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    document.removeEventListener('click', this.#handleOutsideClick);
    document.documentElement.classList.remove('horizon-filter-drawer-open');
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('toggle', this.#handleDrawerToggle, true);
    this.#mobileMedia?.removeEventListener('change', this.#handleViewportChange);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('[data-horizon-filter-input], [data-horizon-sort]')) return;

    if (event.target.matches('[data-horizon-filter-input]') && this.#mobileMedia?.matches) {
      return;
    }

    const form = event.target.closest('[data-horizon-collection-form]');
    if (!(form instanceof HTMLFormElement)) return;

    this.#render(this.#urlFromForm(form), {
      historyMode: 'push',
      focus: event.target.matches('[data-horizon-sort]') ? { type: 'sort' } : this.#controlFocus(event.target),
    });
  };

  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-horizon-collection-form]')) return;

    event.preventDefault();
    this.#closeDrawer(false);
    this.#render(this.#urlFromForm(form), { historyMode: 'push', focus: { type: 'results' } });
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const densityButton = event.target.closest('[data-horizon-grid-density]');
    if (densityButton) {
      this.#setGridDensity(densityButton.dataset.horizonGridDensity);
      return;
    }

    if (event.target.closest('[data-horizon-filter-close]')) {
      event.preventDefault();
      this.#closeDrawer(true);
      return;
    }

    const link = event.target.closest(
      '[data-horizon-filter-link], .horizon-collection-empty__link, .horizon-pagination a'
    );
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    this.#closeDrawer(false);
    const url = sanitizeCollectionURL(link.href);
    const isPagination = link.closest('.horizon-pagination') !== null;
    this.#render(url, {
      historyMode: 'push',
      focus: isPagination ? { type: 'results', scroll: true } : { type: 'results' },
    });
  };

  #handleKeyDown = (event) => {
    if (!this.#mobileMedia?.matches || event.target.closest('[data-horizon-sort-popover]')) {
      if (event.key === 'Escape') {
        const popover = event.target.closest('[data-horizon-facet-popover], [data-horizon-sort-popover]');
        if (popover?.open) {
          event.preventDefault();
          popover.open = false;
          popover.querySelector('summary')?.focus();
        }
      }
      return;
    }

    const disclosure = this.querySelector('[data-horizon-filter-drawer]');
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDrawer(true);
      return;
    }

    if (event.key !== 'Tab') return;

    const panel = disclosure.querySelector('.horizon-collection-facets__panel');
    if (!(panel instanceof HTMLElement)) return;

    const focusable = Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), summary'
      )
    ).filter((element) => element instanceof HTMLElement && element.getClientRects().length > 0);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #handleDrawerToggle = (event) => {
    const disclosure = event.target;
    if (!(disclosure instanceof HTMLDetailsElement)) return;
    if (disclosure.matches('[data-horizon-facet-popover], [data-horizon-sort-popover]')) {
      if (disclosure.matches('[data-horizon-facet-popover]') && !disclosure.closest('.horizon-collection-facets--horizontal')) return;
      if (!this.#mobileMedia?.matches && disclosure.open) {
        disclosure.classList.remove('horizon-collection-facet--align-end');
        const content = disclosure.querySelector('.horizon-collection-facet__content');
        if (content && content.getBoundingClientRect().right > window.innerWidth - 16) {
          disclosure.classList.add('horizon-collection-facet--align-end');
        }
        this.querySelectorAll(DESKTOP_POPOVER_SELECTOR).forEach((other) => {
          if (other !== disclosure) other.open = false;
        });
      }
      return;
    }
    if (!disclosure.matches('[data-horizon-filter-drawer]')) return;
    if (!this.#mobileMedia?.matches) return;

    document.documentElement.classList.toggle('horizon-filter-drawer-open', disclosure.open);
    const panel = disclosure.querySelector('.horizon-collection-facets__panel');
    if (panel instanceof HTMLElement) {
      panel.toggleAttribute('aria-modal', disclosure.open);
      if (disclosure.open) {
        panel.setAttribute('role', 'dialog');
      } else {
        panel.removeAttribute('role');
      }
    }
    if (!disclosure.open) return;
    disclosure.querySelectorAll('[data-horizon-facet-popover]').forEach((facet) => { facet.open = true; });

    requestAnimationFrame(() => panel?.querySelector('[data-horizon-filter-close]')?.focus());
  };

  #handleViewportChange = (event) => {
    this.querySelectorAll('.horizon-collection-facets--horizontal [data-horizon-facet-popover]').forEach((facet) => { facet.open = false; });
    document.documentElement.classList.remove('horizon-filter-drawer-open');
    const disclosure = this.querySelector('[data-horizon-filter-drawer]');
    if (disclosure instanceof HTMLDetailsElement) disclosure.open = !event.matches;
    const panel = this.querySelector('.horizon-collection-facets__panel');
    panel?.removeAttribute('aria-modal');
    panel?.removeAttribute('role');
  };

  #handleOutsideClick = (event) => {
    if (this.#mobileMedia?.matches) return;
    this.querySelectorAll(DESKTOP_POPOVER_SELECTOR).forEach((popover) => {
      if (!popover.contains(event.target)) popover.open = false;
    });
  };

  #setGridDensity(density) {
    this.#gridDensity = density === 'compact' ? 'compact' : 'comfortable';
    const grid = this.querySelector('[data-horizon-collection-grid]');
    if (grid) grid.dataset.gridDensity = this.#gridDensity;
    this.querySelectorAll('[data-horizon-grid-density]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.horizonGridDensity === this.#gridDensity));
    });
  }

  #handlePopState = () => {
    this.#closeDrawer(false);
    this.#render(sanitizeCollectionURL(window.location.href), {
      historyMode: 'none',
      focus: { type: 'results' },
    });
  };

  #urlFromForm(form) {
    return collectionURLFromEntries(form.action, new FormData(form));
  }

  #closeDrawer(restoreFocus) {
    const disclosure = this.querySelector('[data-horizon-filter-drawer]');
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.open || !this.#mobileMedia?.matches) return;

    disclosure.open = false;
    document.documentElement.classList.remove('horizon-filter-drawer-open');
    if (restoreFocus) disclosure.querySelector('summary')?.focus();
  }

  #controlFocus(control) {
    return {
      type: 'control',
      name: control.name,
      value: control.value,
      tagName: control.tagName.toLowerCase(),
    };
  }

  async #render(url, { historyMode, focus }) {
    if (!this.sectionId) {
      window.location.assign(url.toString());
      return;
    }

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    const requestURL = new URL(url);
    requestURL.hash = '';
    requestURL.searchParams.set('section_id', this.sectionId);

    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestURL, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Collection section request failed: ${response.status}`);

      const html = await response.text();
      if (abortController.signal.aborted || this.#abortController !== abortController) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const wrapperId = `shopify-section-${this.sectionId}`;
      const currentWrapper = document.getElementById(wrapperId);
      const nextWrapper = parsedDocument.getElementById(wrapperId);

      if (!currentWrapper || !nextWrapper) throw new Error('Collection section response was incomplete');

      if (historyMode === 'push') history.pushState({}, '', url);
      currentWrapper.replaceChildren(...Array.from(nextWrapper.childNodes).map((node) => node.cloneNode(true)));

      const nextResults = currentWrapper.querySelector(HORIZON_COLLECTION_ELEMENT);
      nextResults?.#setGridDensity(this.#gridDensity);
      requestAnimationFrame(() => this.#restoreFocus(currentWrapper, focus));
    } catch (error) {
      if (abortController.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
      window.location.assign(url.toString());
    } finally {
      if (this.isConnected && this.#abortController === abortController) this.removeAttribute('aria-busy');
    }
  }

  #restoreFocus(wrapper, focus) {
    let target;

    if (focus?.type === 'control' && focus.name) {
      const controls = wrapper.querySelectorAll(`[name="${CSS.escape(focus.name)}"]`);
      target = Array.from(controls).find(
        (control) => control.tagName.toLowerCase() === focus.tagName && control.value === focus.value
      );
    }

    if (focus?.type === 'sort') target = wrapper.querySelector('.horizon-collection-sort__toggle');
    if (!(target instanceof HTMLElement)) {
      target = wrapper.querySelector('[data-horizon-results-focus]');
    }

    if (!(target instanceof HTMLElement)) return;
    if (focus?.type !== 'sort') target.closest('details')?.setAttribute('open', '');
    target.focus({ preventScroll: !focus?.scroll });
    if (focus?.scroll) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get(HORIZON_COLLECTION_ELEMENT)) {
  customElements.define(HORIZON_COLLECTION_ELEMENT, HorizonCollectionResults);
}
